import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, Grid, Card, CardContent, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Avatar, Tooltip, TablePagination
} from '@mui/material';
import Sidebar from '../components/Sidebar';
import {
  People, Add, Edit, Delete, WhatsApp, Phone, LocationOn,
  BookOnline, Visibility
} from '@mui/icons-material';
import api from '../services/api';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All Sources');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDialog, setCustomerDialog] = useState({ open: false, mode: 'view', customer: null });
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', source: '' });

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [page, rowsPerPage, search, sourceFilter]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: {
          page,
          limit: rowsPerPage,
          search,
          source: sourceFilter === 'All Sources' ? undefined : sourceFilter
        }
      });
      setCustomers(response.data.customers);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/customers/stats/overview');
      setStats(response.data);
    } catch {
      console.error('Failed to fetch stats');
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({ name: '', phone: '', email: '', source: '' });
    setCustomerDialog({ open: true, mode: 'add', customer: null });
  };

  const handleOpenEditDialog = (customer) => {
    setFormData({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      source: customer.source || '',
    });
    setCustomerDialog({ open: true, mode: 'edit', customer });
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDialog({ open: true, mode: 'view', customer });
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await api.delete(`/customers/${customerId}`);
        fetchCustomers();
        fetchStats();
      } catch {
        setError('Failed to delete customer');
      }
    }
  };

  const handleSaveCustomer = async () => {
    const { name, phone, email, source } = formData;
    if (!name || !phone || !source) return alert('Name, Phone, and Source are required');
    try {
      if (customerDialog.mode === 'add') {
        await api.post('/customers', formData);
      } else {
        await api.put(`/customers/${customerDialog.customer._id}`, formData);
      }
      setCustomerDialog({ open: false, mode: 'view', customer: null });
      fetchCustomers();
      fetchStats();
    } catch {
      alert('Failed to save customer');
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'whatsapp': return <WhatsApp />;
      case 'phone': return <Phone />;
      case 'walk-in': return <LocationOn />;
      case 'website': return <BookOnline />;
      default: return <People />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'whatsapp': return 'success';
      case 'phone': return 'info';
      case 'walk-in': return 'warning';
      case 'website': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          <People sx={{ mr: 1, verticalAlign: 'middle' }} />
          Customers
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Stats */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Stats Cards */}
          </Grid>
        )}

        {/* Filters */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Search customers" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} label="Source">
                  <MenuItem value="All Sources">All Sources</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="walk-in">Walk-in</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenAddDialog}>
                Add Customer
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Total Bookings</TableCell>
                  {/* <TableCell>Total Spent</TableCell> */}
                  <TableCell>Last Visit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {customer.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{customer.name}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {customer._id.slice(-8)}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{customer.phone}</Typography>
                      {customer.email && (
                        <Typography variant="caption" color="text.secondary">{customer.email}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip icon={getSourceIcon(customer.source)} label={customer.source} color={getSourceColor(customer.source)} size="small" />
                    </TableCell>
                    <TableCell>{customer.totalBookings}</TableCell>
                    {/* <TableCell>${customer.totalSpent}</TableCell> */}
                    <TableCell>{customer.lastVisit ? new Date(customer.lastVisit).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell>
                      <Chip label={customer.isActive ? 'Active' : 'Inactive'} color={customer.isActive ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleCustomerClick(customer)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Customer">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(customer)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Customer">
                          <IconButton size="small" color="error" onClick={() => handleDeleteCustomer(customer._id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Updated Pagination */}
          <TablePagination
            component="div"
            count={totalPages * rowsPerPage}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Paper>

        {/* Customer Dialog */}
        <Dialog
          open={customerDialog.open}
          onClose={() => setCustomerDialog({ open: false, mode: 'view', customer: null })}
          maxWidth="sm"
          fullWidth
        >
          {customerDialog.mode === 'view' && selectedCustomer && (
            <>
              <DialogTitle>Customer Details - {selectedCustomer.name}</DialogTitle>
              <DialogContent>
                <Typography>Name: {selectedCustomer.name}</Typography>
                <Typography>Phone: {selectedCustomer.phone}</Typography>
                <Typography>Email: {selectedCustomer.email || 'N/A'}</Typography>
                <Typography>Source: {selectedCustomer.source}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCustomerDialog({ open: false, mode: 'view', customer: null })}>Close</Button>
              </DialogActions>
            </>
          )}

          {(customerDialog.mode === 'add' || customerDialog.mode === 'edit') && (
            <>
              <DialogTitle>{customerDialog.mode === 'add' ? 'Add Customer' : 'Edit Customer'}</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} style={{width:"-webkit-fill-available"}}>
                    <TextField fullWidth label="Name*" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} style={{width:"-webkit-fill-available"}}>
                    <TextField fullWidth label="Phone*" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} style={{width:"-webkit-fill-available"}}>
                    <TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} style={{width:"-webkit-fill-available"}}>
                    <FormControl fullWidth >
                      <InputLabel>Source*</InputLabel>
                      <Select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} label="Source">
                        <MenuItem value="whatsapp">WhatsApp</MenuItem>
                        <MenuItem value="walk-in">Walk-in</MenuItem>
                        <MenuItem value="phone">Phone</MenuItem>
                        <MenuItem value="website">Website</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCustomerDialog({ open: false, mode: 'view', customer: null })}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveCustomer}>
                  {customerDialog.mode === 'add' ? 'Add' : 'Update'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Box>
  );
};

export default Customers;
